<template>
  <div class="profile-360">
    <v-skeleton-loader v-if="userState.loading" type="card, card" data-test="profile-loading" />

    <v-card v-else-if="userState.error || !userInfo" class="seccion-card">
      <SourceError360 label="los datos del tutor" @retry="$emit('retry', 'user')" />
    </v-card>

    <div v-else class="profile-layout">
      <!-- Columna principal: datos, co-tutores y nota. -->
      <div class="profile-main">
        <v-card class="seccion-card" data-test="section-data">
          <v-card-title class="section-head">
            Datos del tutor
            <v-btn
              v-if="can('users:update')"
              small
              outlined
              data-test="edit-user"
              @click="$emit('edit-user')"
            >Editar</v-btn>
          </v-card-title>
          <dl class="field-grid">
            <div
              v-for="field in dataFields"
              :key="field.key"
              class="field"
              :class="{ 'is-wide': field.wide }"
              :data-test="`field-${field.key}`"
            >
              <dt class="label">{{ field.label }}</dt>
              <dd class="value">{{ userInfo[field.key] || '—' }}</dd>
            </div>
          </dl>
        </v-card>

        <v-card class="seccion-card" data-test="section-co-tutors">
          <v-card-title class="section-head">
            Co-tutores
            <v-btn
              v-if="canAct('co-tutors:create')"
              small
              outlined
              data-test="add-co-tutor"
              @click="$emit('add-co-tutor')"
            >
              <v-icon left small>mdi-plus</v-icon>Agregar co-tutor
            </v-btn>
          </v-card-title>
          <div v-if="!coTutors.length" class="empty-state" data-test="co-tutors-empty">Sin co-tutores.</div>
          <div v-else class="table-scroll">
            <table class="co-tutors-table" data-test="co-tutors-table">
              <thead>
                <tr>
                  <th scope="col">Nombre</th>
                  <th scope="col">RUT / DNI</th>
                  <th scope="col">Relación</th>
                  <th v-if="canSeeCoTutorDocuments" scope="col">Archivo</th>
                  <th scope="col"><span class="sr-only">Acciones</span></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(coTutor, index) in coTutors" :key="coTutor.id || index" data-test="co-tutor-row">
                  <td class="row-title">{{ coTutor.firstName }} {{ coTutor.lastName }}</td>
                  <td class="pw-num">{{ coTutor.rut || '—' }}</td>
                  <td>{{ relationshipLabel(coTutor.relationshipType) }}</td>
                  <td v-if="canSeeCoTutorDocuments">
                    <a v-if="coTutor.fileUrl" :href="coTutor.fileUrl" target="_blank" rel="noopener">Ver archivo</a>
                    <span v-else class="row-sub">—</span>
                  </td>
                  <td class="cell-action">
                    <v-btn
                      v-if="canAct('co-tutors:update')"
                      small
                      outlined
                      :aria-label="`Editar a ${coTutor.firstName} ${coTutor.lastName}`"
                      data-test="edit-co-tutor"
                      @click="$emit('edit-co-tutor', coTutor)"
                    >Editar</v-btn>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </v-card>

        <v-card v-if="canSeeSecurity" class="seccion-card" data-test="section-note">
          <v-card-title class="section-head">
            Nota interna
            <v-btn
              v-if="can('users:update')"
              small
              outlined
              data-test="edit-note"
              @click="$emit('edit-note')"
            >
              {{ userInfo.note ? 'Editar' : 'Agregar' }}
            </v-btn>
          </v-card-title>
          <div class="card-body">
            <div class="label">Solo visible para el equipo Pawer</div>
            <p class="note" :class="{ 'is-empty': !userInfo.note }" data-test="note">{{ userInfo.note || 'Sin nota interna.' }}</p>
          </div>
        </v-card>
      </div>

      <!-- Columna lateral: señales de riesgo y accesos. -->
      <div class="profile-side">
        <v-card class="seccion-card" data-test="section-risk">
          <v-card-title class="section-head">Señales de riesgo</v-card-title>
          <ul class="item-rows">
            <li v-if="canSeeSecurity" class="item-row" :class="{ 'signal-error': blacklist }" data-test="signal-blacklist">
              <span>
                <span class="row-title">Lista negra</span>
                <span class="row-sub">
                  {{ blacklist ? blacklist.reason || 'Sin motivo registrado' : 'Bloquea nuevas contrataciones' }}
                </span>
              </span>
              <v-btn
                v-if="can('users:blacklist')"
                small
                outlined
                color="error"
                data-test="blacklist"
                @click="$emit('blacklist')"
              >
                {{ blacklist ? 'Editar' : 'Agregar' }}
              </v-btn>
            </li>
            <li v-if="canSeeSecurity" class="item-row" :class="{ 'signal-warning': suspicious }" data-test="signal-suspicious">
              <span>
                <span class="row-title">Sospechoso</span>
                <span class="row-sub" data-test="suspicious-reason">
                  {{ suspicious ? suspicious.reason || 'Sin motivo registrado' : 'Pide revisión manual de reembolsos' }}
                </span>
              </span>
              <v-btn
                v-if="can('users:flag-suspicious')"
                small
                outlined
                color="error"
                data-test="suspicious"
                @click="$emit('suspicious')"
              >
                {{ suspicious ? 'Editar' : 'Marcar' }}
              </v-btn>
            </li>
            <li class="item-row" data-test="signal-identity">
              <span>
                <span class="row-title">Identidad</span>
                <span class="row-sub">{{ identityText }}</span>
                <span v-if="identityError" class="identity-error" role="alert" data-test="identity-error">
                  No se pudo validar la identidad.
                </span>
              </span>
              <v-chip
                v-if="userInfo.identityValidatedAt"
                small
                outlined
                color="green"
                class="font-weight-bold"
                data-test="identity-validated"
              >Verificada</v-chip>
              <v-btn
                v-else-if="can('users:validate-identity')"
                small
                depressed
                color="primary"
                :loading="validatingIdentity"
                data-test="validate-identity"
                @click="validateIdentity"
              >Validar</v-btn>
            </li>
          </ul>
        </v-card>

        <v-card class="seccion-card" data-test="section-access">
          <v-card-title class="section-head">Accesos</v-card-title>
          <div class="card-body">
            <dl class="access-grid">
              <div>
                <dt class="label">Último acceso</dt>
                <dd class="value pw-num" data-test="last-access">{{ lastAccessText }}</dd>
              </div>
              <div>
                <dt class="label">Método</dt>
                <dd class="value" data-test="login-method">Contraseña</dd>
              </div>
              <div>
                <dt class="label">Cuenta creada</dt>
                <dd class="value pw-num" data-test="account-created">{{ accountCreatedText }}</dd>
              </div>
              <div>
                <dt class="label">Dispositivos</dt>
                <dd class="value" data-test="devices">{{ devicesText }}</dd>
              </div>
            </dl>
            <div class="access-actions">
              <v-btn
                v-if="can('users:reset-password')"
                small
                outlined
                data-test="reset-password"
                @click="$emit('reset-password')"
              >
                Reiniciar contraseña
              </v-btn>
              <v-btn
                v-if="can('users:force-logout')"
                small
                outlined
                color="error"
                data-test="force-logout"
                @click="$emit('force-logout')"
              >
                Cerrar todas las sesiones
              </v-btn>
            </div>
          </div>
        </v-card>
      </div>
    </div>

  </div>
</template>

<script>
import SourceError360 from './SourceError360.vue';
import { displayRelationshipType } from '../utils/relationship-type.utils';
import UsersService from '../services/users.service';
import RutUtils from '../utils/rut.utils';
import { can, canAct } from '../utils/permissions';

// Datos del tutor en el orden del prototipo (3 columnas); la dirección ocupa la fila completa.
const DATA_FIELDS = [
  { key: 'name', label: 'Nombre' },
  { key: 'rut', label: 'RUT' },
  { key: 'birthdate', label: 'Fecha de nacimiento' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Teléfono' },
  { key: 'country', label: 'País' },
  { key: 'address', label: 'Dirección', wide: true }
];

// Las fechas de acceso se muestran en hora de Chile, sin depender de la zona del navegador.
const CHILE_TZ = 'America/Santiago';
const pad = (n) => String(n).padStart(2, '0');
const chileParts = (iso, options) =>
  Object.fromEntries(
    new Intl.DateTimeFormat('es-CL', { timeZone: CHILE_TZ, hourCycle: 'h23', ...options })
      .formatToParts(new Date(iso))
      .map((part) => [part.type, part.value])
  );
const formatChileDate = (iso) => {
  const { day, month, year } = chileParts(iso, { day: 'numeric', month: 'numeric', year: 'numeric' });
  return `${pad(day)}/${pad(month)}/${String(year).slice(-2)}`;
};
const formatChileTime = (iso) => {
  const { hour, minute } = chileParts(iso, { hour: 'numeric', minute: 'numeric' });
  return `${pad(hour)}:${pad(minute)}`;
};

const PLATFORM_LABELS = { ios: 'iOS', android: 'Android' };

// Una señal cuenta como vigente si existe y no viene marcada explícitamente como inactiva.
const activeSignal = (signal) => (signal && signal.active !== false ? signal : null);

export default {
  name: 'Profile360Tab',
  components: { SourceError360 },
  props: {
    // Forma de dataMixin.buildUserInfo; null si la carga del tutor falló.
    userInfo: { type: Object, default: null },
    // Señales que carga el contenedor: { blacklist: { reason } | null, suspicious: { reason } | null }.
    riskSignals: { type: Object, default: () => ({ blacklist: null, suspicious: null }) },
    userId: { type: String, default: null },
    token: { type: String, default: null },
    // Estado de carga por fuente del contenedor (FR-022); aquí solo importa `user`.
    loadState: { type: Object, default: () => ({ user: { loading: false, error: false } }) }
  },
  data() {
    return {
      validatingIdentity: false,
      identityError: false
    };
  },
  computed: {
    // El rótulo del documento sigue al país del tutor: RUT en Chile, DNI en el resto.
    dataFields() {
      const identifierLabel = RutUtils.identifierLabel(this.userInfo?.country);
      return DATA_FIELDS.map((field) => (field.key === 'rut' ? { ...field, label: identifierLabel } : field));
    },
    canSeeCoTutorDocuments() {
      return canAct('co-tutors:view-documents');
    },
    // Nota interna, lista negra y marca de sospechoso.
    canSeeSecurity() {
      return can('users:view-security');
    },
    userState() {
      return this.loadState?.user || { loading: false, error: false };
    },
    coTutors() {
      return this.userInfo?.coTutors || [];
    },
    blacklist() {
      return activeSignal(this.riskSignals?.blacklist);
    },
    suspicious() {
      return activeSignal(this.riskSignals?.suspicious);
    },
    // Con fecha y hora de Chile si viene la fecha completa; si no, el DD/MM/AA de siempre.
    lastAccessText() {
      const at = this.userInfo?.lastAccessAt;
      if (!at) return this.userInfo?.lastAccess && this.userInfo.lastAccess !== '-' ? this.userInfo.lastAccess : '—';
      return `${formatChileDate(at)} · ${formatChileTime(at)}`;
    },
    accountCreatedText() {
      const at = this.userInfo?.accountCreatedAt;
      return at ? formatChileDate(at) : '—';
    },
    // «3 dispositivos» y, debajo del número, el detalle por plataforma («2 Android · 1 iOS»).
    devicesText() {
      const devices = this.userInfo?.devices || [];
      const total = devices.reduce((sum, d) => sum + (Number(d.count) || 0), 0);
      if (!total) return 'Ninguno';
      const label = total === 1 ? '1 dispositivo' : `${total} dispositivos`;
      const detail = devices
        .filter((d) => d.count > 0)
        .map((d) => `${d.count} ${PLATFORM_LABELS[d.platform] || 'Otro'}`)
        .join(' · ');
      return `${label} (${detail})`;
    },
    identityText() {
      const validatedAt = this.userInfo?.identityValidatedAt;
      return validatedAt ? `Verificada el ${this.formatDate(validatedAt)}` : 'Sin verificar';
    }
  },
  methods: {
    can,
    canAct,
    relationshipLabel: displayRelationshipType,
    formatDate(isoString) {
      if (!isoString) return '';
      return new Date(isoString).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    // Mismo flujo que tenía CardsDetails360: valida y avisa al contenedor con la fecha, que
    // actualiza userInfo (no se muta la prop aquí).
    async validateIdentity() {
      const userId = this.userId || this.userInfo?.userId;
      if (!userId) return;
      this.validatingIdentity = true;
      this.identityError = false;
      try {
        const res = await UsersService.validateIdentity(userId);
        if (res.status === 'ok') {
          this.$emit('identity-validated', new Date().toISOString());
        } else {
          this.identityError = true;
        }
      } finally {
        this.validatingIdentity = false;
      }
    }
  }
};
</script>

<style scoped>
/* Prototipo: columna principal ancha y lateral angosta; en pantallas chicas se apilan. */
.profile-layout {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(300px, 1fr);
  gap: 0 20px;
  align-items: start;
}
@media (max-width: 1100px) {
  .profile-layout {
    grid-template-columns: minmax(0, 1fr);
  }
}
.seccion-card {
  margin-bottom: 20px;
  font-size: 0.84rem;
}
.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  padding: 12px 20px;
  border-bottom: 1px solid var(--pw-divider);
}
.card-body {
  padding: 14px 20px 18px;
}

.field-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px 20px;
  margin: 0;
  padding: 16px 20px 20px;
}
@media (max-width: 700px) {
  .field-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
.field.is-wide {
  grid-column: 1 / -1;
}
.label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.045em;
  color: var(--pw-text-muted);
  margin-bottom: 3px;
}
.value {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--pw-text);
  word-break: break-word;
}
.empty-state {
  padding: 16px 20px;
  color: var(--pw-text-muted);
}

/* Filas con título, subtítulo y acción a la derecha (co-tutores y señales de riesgo). */
.item-rows {
  list-style: none;
  margin: 0;
  padding: 0;
}
.item-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 20px;
  border-bottom: 1px solid var(--pw-divider);
}
.item-row:last-child {
  border-bottom: 0;
}
.row-title {
  display: block;
  font-weight: 600;
  color: var(--pw-text);
}
.row-sub {
  display: block;
  font-size: 0.78rem;
  color: var(--pw-text-muted);
}
.signal-error {
  background-color: var(--pw-error-tint);
}
.signal-error .row-title {
  color: var(--pw-error);
}
.signal-warning {
  background-color: var(--pw-warning-tint);
}
.signal-warning .row-title {
  color: var(--pw-warning);
}
.identity-error {
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--pw-error);
}

.table-scroll {
  overflow-x: auto;
}
.co-tutors-table {
  width: 100%;
  border-collapse: collapse;
}
.co-tutors-table th {
  padding: 10px 20px 6px;
  text-align: left;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.045em;
  color: var(--pw-text-muted);
  border-bottom: 1px solid var(--pw-divider);
}
.co-tutors-table td {
  padding: 10px 20px;
  border-bottom: 1px solid var(--pw-divider);
}
.co-tutors-table tr:last-child td {
  border-bottom: 0;
}
.co-tutors-table .cell-action {
  text-align: right;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}

.note {
  margin: 0;
  padding: 10px 12px;
  white-space: pre-wrap;
  color: var(--pw-text);
  border: 1px solid var(--pw-border);
  border-radius: var(--pw-radius-control);
}
.note.is-empty {
  color: var(--pw-text-muted);
}

.access-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 16px;
  margin: 0;
}
.access-grid dd {
  margin: 0;
}
.access-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  margin-top: 14px;
}
</style>
